import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import { getAccessToken } from "../../api/getAccessToken";
import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";

export type SportsbookTranslationStatus =
  | "translated"
  | "pending"
  | "missing"
  | "stale";

export type SportsbookTranslationOverrideMeta = {
  updated_by: string;
  updated_at: string;
};

export type SportsbookTranslationLocale = {
  locale: string;
  machine: string | null;
  override: string | null;
  status: SportsbookTranslationStatus;
  override_meta?: SportsbookTranslationOverrideMeta;
};

export type SportsbookTranslation = {
  entity_key: string;
  base: {
    locale: string;
    value: string;
  };
  locales: SportsbookTranslationLocale[];
};

export type UpdateSportsbookTranslationInput = {
  entityKey: string;
  locale: string;
  value: string;
};

export type DeleteSportsbookTranslationInput = Omit<
  UpdateSportsbookTranslationInput,
  "value"
>;

const translationStatuses: readonly SportsbookTranslationStatus[] = [
  "translated",
  "pending",
  "missing",
  "stale",
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isNullableString = (value: unknown): value is string | null =>
  typeof value === "string" || value === null;

const isOverrideMeta = (
  value: unknown,
): value is SportsbookTranslationOverrideMeta =>
  isRecord(value) &&
  typeof value.updated_by === "string" &&
  typeof value.updated_at === "string";

export function parseSportsbookTranslation(
  value: unknown,
): SportsbookTranslation {
  if (
    !isRecord(value) ||
    typeof value.entity_key !== "string" ||
    !isRecord(value.base) ||
    typeof value.base.locale !== "string" ||
    typeof value.base.value !== "string" ||
    !Array.isArray(value.locales)
  ) {
    throw new Error("Invalid sportsbook translation response");
  }

  const locales = value.locales.map((locale) => {
    if (
      !isRecord(locale) ||
      typeof locale.locale !== "string" ||
      !isNullableString(locale.machine) ||
      !isNullableString(locale.override) ||
      typeof locale.status !== "string" ||
      !translationStatuses.includes(locale.status as SportsbookTranslationStatus) ||
      (locale.override_meta !== undefined && !isOverrideMeta(locale.override_meta))
    ) {
      throw new Error("Invalid sportsbook translation response");
    }

    return {
      locale: locale.locale,
      machine: locale.machine,
      override: locale.override,
      status: locale.status as SportsbookTranslationStatus,
      ...(locale.override_meta === undefined
        ? {}
        : { override_meta: locale.override_meta }),
    };
  });

  return {
    entity_key: value.entity_key,
    base: {
      locale: value.base.locale,
      value: value.base.value,
    },
    locales,
  };
}

export const sportsbookTranslationQueryKey = (entityKey: string) =>
  ["sportsbookFm", "translations", entityKey] as const;

const translationRoute = (
  routeTemplate: string,
  entityKey: string,
  locale?: string,
) =>
  routeTemplate.replace(
    "{entity_key}",
    encodeURIComponent(entityKey),
  ).replace("{locale}", locale === undefined ? "" : encodeURIComponent(locale));

function validatePathInput(entityKey: string, locale?: string) {
  if (!entityKey || (locale !== undefined && !locale)) {
    throw new Error("A translation entity key and locale are required");
  }
}

export async function fetchSportsbookTranslation(
  entityKey: string,
): Promise<SportsbookTranslation> {
  validatePathInput(entityKey);
  const client = createPrivateAdminPanelInstance(getAccessToken());
  const response = await client.get(
    translationRoute(API_ROUTES.GET_SPORTSBOOK_FM_TRANSLATION, entityKey),
  );
  return parseSportsbookTranslation(response.data);
}

export type UseSportsbookTranslationOptions = {
  /**
   * Seeds the cache when the caller already has the translation payload
   * (e.g. the sports-ordering list response embeds it).
   */
  initialData?: SportsbookTranslation;
  enabled?: boolean;
};

export function useSportsbookTranslation(
  entityKey?: string,
  options?: UseSportsbookTranslationOptions,
) {
  return useQuery({
    queryKey: sportsbookTranslationQueryKey(entityKey ?? ""),
    queryFn: () => fetchSportsbookTranslation(entityKey as string),
    enabled: Boolean(entityKey) && options?.enabled !== false,
    initialData: options?.initialData,
  });
}

export async function updateSportsbookTranslation({
  entityKey,
  locale,
  value,
}: UpdateSportsbookTranslationInput): Promise<SportsbookTranslation> {
  validatePathInput(entityKey, locale);
  const client = createPrivateAdminPanelInstance(getAccessToken());
  const response = await client.put(
    translationRoute(API_ROUTES.UPDATE_SPORTSBOOK_FM_TRANSLATION, entityKey, locale),
    { value },
  );
  return parseSportsbookTranslation(response.data);
}

export async function deleteSportsbookTranslation({
  entityKey,
  locale,
}: DeleteSportsbookTranslationInput): Promise<SportsbookTranslation> {
  validatePathInput(entityKey, locale);
  const client = createPrivateAdminPanelInstance(getAccessToken());
  const response = await client.delete(
    translationRoute(API_ROUTES.DELETE_SPORTSBOOK_FM_TRANSLATION, entityKey, locale),
  );
  return parseSportsbookTranslation(response.data);
}

function updateTranslationCache(
  queryClient: ReturnType<typeof useQueryClient>,
  entityKey: string,
  translation: SportsbookTranslation,
) {
  queryClient.setQueryData(
    sportsbookTranslationQueryKey(entityKey),
    translation,
  );
  void queryClient.invalidateQueries({
    queryKey: sportsbookTranslationQueryKey(entityKey),
  });
}

export function useUpdateSportsbookTranslation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["sportsbookFm", "translations", "update"],
    mutationFn: updateSportsbookTranslation,
    onSuccess: (translation, { entityKey }) =>
      updateTranslationCache(queryClient, entityKey, translation),
  });
}

export function useDeleteSportsbookTranslation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["sportsbookFm", "translations", "delete"],
    mutationFn: deleteSportsbookTranslation,
    onSuccess: (translation, { entityKey }) =>
      updateTranslationCache(queryClient, entityKey, translation),
  });
}
